import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Minimize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import './WheelGame.css';

const WheelGame = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const wheelId = searchParams.get('wheelId');
  const iframeRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const containerRef = useRef(null);
  const toolbarTimeoutRef = useRef(null);

  const saveWheel = (wheelData) => {
    try {
      const data = localStorage.getItem('wheelDatabase');
      const wheels = data ? JSON.parse(data) : [];
      
      const newWheel = {
        id: wheelData.id || Date.now().toString(),
        title: wheelData.title || 'Lucky Wheel',
        slices: wheelData.slices || [],
        createdAt: wheelData.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Check if wheel already exists
      const existingIndex = wheels.findIndex(w => w.id === newWheel.id);
      if (existingIndex >= 0) {
        wheels[existingIndex] = newWheel;
      } else {
        wheels.push(newWheel);
      }

      localStorage.setItem('wheelDatabase', JSON.stringify(wheels));
      toast.success('Wheel saved successfully!');
    } catch (error) {
      console.error('Error saving wheel:', error);
      toast.error('Failed to save wheel');
    }
  };

  const exitFullscreenAndNavigate = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
    navigate('/wheel-hub');
  };

  const exitFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (error) {
      console.log('Exit fullscreen failed:', error);
    }
  };

  // Auto-enter fullscreen on mount
  useEffect(() => {
    const enterFullscreen = async () => {
      try {
        if (containerRef.current && !document.fullscreenElement) {
          await containerRef.current.requestFullscreen();
          setIsFullscreen(true);
        }
      } catch (error) {
        console.log('Fullscreen request failed:', error);
      }
    };

    // Small delay to ensure component is mounted
    const timer = setTimeout(enterFullscreen, 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // If we have a wheelId, load the wheel data and send it to the iframe
    if (wheelId && iframeRef.current) {
      const sendWheelData = () => {
        const data = localStorage.getItem('wheelDatabase');
        const wheels = data ? JSON.parse(data) : [];
        const wheel = wheels.find(w => w.id === wheelId);
        
        if (wheel && iframeRef.current?.contentWindow) {
          console.log('📤 Sending wheel data to iframe:', wheel.title);
          iframeRef.current.contentWindow.postMessage({
            type: 'LOAD_WHEEL',
            wheel: wheel
          }, '*');
        }
      };
      
      // Send after iframe loads
      if (iframeRef.current) {
        iframeRef.current.onload = () => {
          setTimeout(sendWheelData, 500);
        };
      }
    }

    // Listen for messages from the iframe
    const handleMessage = (event) => {
      if (event.data.type === 'SAVE_WHEEL') {
        saveWheel(event.data.wheel);
      } else if (event.data.type === 'EXIT_GAME') {
        exitFullscreenAndNavigate();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [wheelId, navigate]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Handle toolbar visibility - show on hover at top edge
  const handleToolbarAreaHover = () => {
    setShowToolbar(true);
    
    // Clear existing timeout
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
  };

  const handleToolbarMouseEnter = () => {
    if (toolbarTimeoutRef.current) {
      clearTimeout(toolbarTimeoutRef.current);
    }
    setShowToolbar(true);
  };

  const handleToolbarMouseLeave = () => {
    toolbarTimeoutRef.current = setTimeout(() => {
      setShowToolbar(false);
    }, 1000);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toolbarTimeoutRef.current) {
        clearTimeout(toolbarTimeoutRef.current);
      }
    };
  }, []);

  return (
    <div className="wheel-game-page" ref={containerRef}>
      {/* Small trigger area at top edge - hover to show toolbar */}
      <div 
        className="toolbar-trigger-area"
        onMouseEnter={handleToolbarAreaHover}
      />
      
      {/* Transparent auto-hide toolbar */}
      <div 
        className={`game-toolbar ${showToolbar ? 'visible' : 'hidden'}`}
        onMouseEnter={handleToolbarMouseEnter}
        onMouseLeave={handleToolbarMouseLeave}
      >
        <Button 
          onClick={exitFullscreenAndNavigate} 
          className="back-button"
          variant="ghost"
        >
          <ArrowLeft size={20} />
          Back to Hub
        </Button>
        <div className="toolbar-title">
          <span className="toolbar-icon">🎡</span>
          <span>Lucky Learning Wheel</span>
        </div>
        <div className="toolbar-actions">
          {isFullscreen && (
            <Button onClick={exitFullscreen} variant="ghost" className="fullscreen-btn">
              <Minimize2 size={20} />
              Exit Fullscreen
            </Button>
          )}
        </div>
      </div>
      
      <div className="game-container">
        <iframe
          ref={iframeRef}
          src="/lucky-learning-wheel.html"
          title="Lucky Learning Wheel"
          className="game-iframe"
          allow="fullscreen"
        />
      </div>
    </div>
  );
};

export default WheelGame;
